"use client";

import React, { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import {
  SnackbarProvider,
  type SnackbarKey,
  type SnackbarProvider as SnackbarProviderType,
} from "notistack";
import { CircleX } from "lucide-react";
import { QueryProvider } from "@/src/core/query/provider";
import { LoginSystem } from "@/src/features/auth";
import { WishlistSheets } from "@/src/shared/features/wishlist";
import HeroFlight from "@/src/shared/components/listing/HeroFlight";
import DetailsLoading from "@/src/shared/components/listing/DetailsLoading";
import {
  NAVIGATION_START_EVENT,
  announceNavigation,
  linkFromPage,
  clickFollowsLink,
  markClientNavigation,
} from "@/src/shared/utils/navigationHistory";

/** A navigation that never arrives (an error, offline) stops showing as loading. */
const LOADING_BAR_TIMEOUT_MS = 15_000;
import { installImageCdnFallback } from "@/src/shared/utils/imageCdnFallback";

/**
 * Everything every page sits in, on the client: server state, snackbars, the login
 * system, the wishlist sheets every heart opens and the card → details photo flight.
 */
const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const notistackRef = useRef<SnackbarProviderType>(null);
  const pathname = usePathname();
  const [isRouteLoading, setIsRouteLoading] = useState(false);

  // Staging carries production rows whose images live in the prod bucket: retry those on
  // the prod host before any placeholder takes over.
  useEffect(() => installImageCdnFallback(), []);

  // The server's loader covers the page until the app is interactive.
  useEffect(() => {
    const loader = document.getElementById("globalLoader");
    if (loader) loader.style.display = "none";
  }, []);

  // Where each navigation started from, for analytics' `from_page` on the page it opens.
  // Recorded on the link's click, before the next page's own effects read it.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      const link = target?.closest?.("a[href]");
      if (!target || !(link instanceof HTMLAnchorElement)) return;
      // A card's heart or photo arrows sit inside its link but don't follow it.
      if (!clickFollowsLink(target, link)) return;
      if (link.origin !== window.location.origin || link.target === "_blank")
        return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey) return;
      announceNavigation(link.href, linkFromPage(link));
    };
    document.addEventListener("click", onClick, true);
    // A back / forward is a navigation too: the page on screen is no longer the one the
    // browser loaded, so it isn't a reload.
    window.addEventListener("popstate", markClientNavigation);
    return () => {
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("popstate", markClientNavigation);
    };
  }, []);

  // The top loading bar, from a navigation's start until its page arrives. Chat moves
  // between threads without it, as before.
  useEffect(() => {
    let giveUp = 0;
    const onStart = (event: Event) => {
      const to = (event as CustomEvent<string>).detail;
      if (to.startsWith("/chat")) return;
      setIsRouteLoading(true);
      window.clearTimeout(giveUp);
      giveUp = window.setTimeout(
        () => setIsRouteLoading(false),
        LOADING_BAR_TIMEOUT_MS,
      );
    };
    window.addEventListener(NAVIGATION_START_EVENT, onStart);
    return () => {
      window.removeEventListener(NAVIGATION_START_EVENT, onStart);
      window.clearTimeout(giveUp);
    };
  }, []);
  useEffect(() => setIsRouteLoading(false), [pathname]);

  const dismiss = (key: SnackbarKey) => () =>
    notistackRef.current?.closeSnackbar(key);

  return (
    <QueryProvider>
      {isRouteLoading && <div className="route-loading-bar" />}
      <SnackbarProvider
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        ref={notistackRef}
        action={(key) => (
          <button
            type="button"
            onClick={dismiss(key)}
            aria-label="Dismiss"
            className="p-2 text-white"
          >
            <CircleX />
          </button>
        )}
      >
        <LoginSystem>
          {children}
          {/* The save / create wishlist sheets every heart opens. */}
          <WishlistSheets />
          {/* Phone: a card's photo flies into its details page, as in the app. */}
          <HeroFlight />
          {/* Desktop: the details page's shape at once, while it loads. */}
          <DetailsLoading />
        </LoginSystem>
      </SnackbarProvider>
    </QueryProvider>
  );
};

export default AppShell;
